//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { RequestEventArgs, Message } from "./Events";
import { v1 as uuidv1 } from "uuid";

class Subscription {
  id: string;
  url: string | undefined;
  callback: () => void;

  constructor(id: string, url: string | undefined, callback: () => void) {
    this.id = id;
    this.url = url;
    this.callback = callback;
  }
}

export interface IWebSocketRef {
  getWebSocket(): any;
}

export class EventSubscriptionManager {
  private ref: IWebSocketRef;

  private subscriptions = new Array<Subscription>();

  private timerRef: NodeJS.Timeout;
  /**
   * Creates a new EventSubscriptionManager
   */
  constructor(ref: IWebSocketRef) {
    this.ref = ref;

    // Call handleServerEvent every 2 minutes once.
    // Notice that the event meganism doesn't have a QoS, so we use this as fallback
    this.timerRef = setInterval(this.handleServerEvent, 2 * 60 * 1000);
  }

  stopTimer = () => {
    clearInterval(this.timerRef);
  };

  // webSocketURL builds the URL of the event websocket
  webSocketURL = (): string => {
    let protocol = window.location.protocol == "https:" ? "wss:" : "ws:";
    let wsUrl = `${protocol}//${window.location.host}/ws/dashboard/v1/events`;
    return wsUrl;
  };

  // handleServerEvent is called when the server sends a message into the websocket.
  handleServerEvent = (data: any) => {
    //console.log(data);
    this.subscriptions.forEach((s) => s.callback());
  };

  closeWSConnection = () => {
    const wsRef = this.ref.getWebSocket() || {};
    const { ws } = wsRef.state || {};
    if (ws && ws.readyState !== 0) {
      console.log(`Closing websocket with URL - ${this.webSocketURL()}`);
      ws.close();
    }
  };

  subscribe = async (args: RequestEventArgs, callback: () => void): Promise<string> => {
    const id = await this.requestEvents(args);
    const foundSubscription = this.subscriptions.find((s) => s.url === args.resource_url);
    if (args.resource_url && args.resource_url !== "" && foundSubscription) {
      foundSubscription.callback = callback;
      return "";
    }
    this.subscriptions.push(new Subscription(id, args.resource_url, callback));
    return id;
  };

  unsubscribe = async (subscriptionID?: string) => {
    if (subscriptionID) {
      this.subscriptions = this.subscriptions.filter((s) => s.id !== subscriptionID);
      await this.cancelEvents(subscriptionID);
    }
  };

  // requestEvents requests events according to the given arguments.
  // An event subscription ID is returned.
  // To cancel events, pass this subscription ID to cancelEvents.
  private requestEvents = async (args: RequestEventArgs): Promise<string> => {
    let id = uuidv1();
    let msg = {
      type: "RequestEvents",
      subscription_id: id,
      args: args,
    } as Message;
    await this.ref.getWebSocket().sendMessage(JSON.stringify(msg));
    return id;
  };

  // cancelEvents cancels all events send by the server resulting from
  // a call to requestEvents.
  private cancelEvents = async (subscriptionID: string) => {
    let msg = {
      type: "CancelEvents",
      subscription_id: subscriptionID,
    } as Message;
    await this.ref.getWebSocket().sendMessage(JSON.stringify(msg));
  };
}
